import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import logo from "@/assets/logo_medium.png";
import { onboardingCopy } from "@/lib/onboarding-copy";
import { supabase } from "@/integrations/supabase/client";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";

const ConfirmEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState<string>("");
  const [isResending, setIsResending] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    // Pegar email da URL ou do localStorage
    const emailFromUrl = searchParams.get("email");
    const emailFromStorage = localStorage.getItem("pending_email_confirmation");
    
    const userEmail = emailFromUrl || emailFromStorage || "";
    setEmail(userEmail);

    // Verificar se o usuário já está verificado
    checkEmailVerification();
  }, [searchParams]);

  const checkEmailVerification = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      // Verificar se email está confirmado no banco
      const { data: profile } = await supabase
        .from("profiles")
        .select("email_confirmed")
        .eq("id", session.user.id)
        .single();

      if (profile?.email_confirmed) {
        setIsVerified(true);
        // Limpar email pendente do localStorage
        localStorage.removeItem("pending_email_confirmation");
      }
    }
  };

  const handleResendEmail = async () => {
    if (!email) {
      toast.error("E-mail não encontrado. Por favor, faça o cadastro novamente.");
      navigate("/onboarding");
      return;
    }

    setIsResending(true);

    try {
      // Buscar user_id do email
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user?.email || !session.access_token) {
        toast.error("Sessão expirada. Por favor, faça login novamente.");
        navigate("/auth");
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const response = await fetch(`${supabaseUrl}/functions/v1/send-confirmation-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: anonKey,
        },
        body: JSON.stringify({
          user_id: session.user.id,
          email: session.user.email,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || "Erro ao reenviar e-mail");
        return;
      }

      toast.success(onboardingCopy.confirm.resendSuccess);
    } catch (error) {
      console.error("Error resending email:", error);
      toast.error("Erro ao reenviar e-mail. Tente novamente.");
    } finally {
      setIsResending(false);
    }
  };

  // Se já está verificado, mostrar mensagem de sucesso
  if (isVerified) {
    return (
      <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
        <div className="w-full max-w-md animate-fade-in">
          <div className="flex justify-center mb-8">
            <img src={logo} alt="Elinia" className="h-8" />
          </div>

          <Card className="shadow-medium">
            <CardContent className="p-8 text-center">
              <div className="flex justify-center mb-4">
                <CheckCircle2 className="h-16 w-16 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-primary mb-2">
                E-mail confirmado!
              </h1>
              <p className="text-muted-foreground mb-6">
                Sua conta foi ativada com sucesso. Continue a configuração.
              </p>
              <Button
                onClick={() => navigate("/onboarding")}
                className="w-full"
                size="lg"
              >
                Continuar configuração
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="flex justify-center mb-8">
          <img src={logo} alt="Elinia" className="h-8" />
        </div>

        <Card className="shadow-medium">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                <Mail className="h-12 w-12 text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-primary mb-2">
                {onboardingCopy.confirm.title}
              </h1>
              <p className="text-muted-foreground">
                {onboardingCopy.confirm.description.replace("{{email}}", email || "seu e-mail")}
              </p>
            </div>

            <div className="space-y-4">
              <Button
                onClick={handleResendEmail}
                className="w-full"
                size="lg"
                variant="outline"
                disabled={isResending}
              >
                {isResending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    {onboardingCopy.confirm.resendButton}
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                {onboardingCopy.confirm.spamNote}
              </p>

              <div className="pt-4 border-t">
                <Button
                  onClick={() => navigate("/onboarding")}
                  variant="ghost"
                  className="w-full"
                >
                  Já confirmei meu e-mail → Continuar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <a
            href="/"
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            ← Voltar à página principal
          </a>
        </div>
      </div>
    </div>
  );
};

export default ConfirmEmail;

